/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Homepage route — _app._index.tsx
 *
 * Content Slot migration strategy
 * --------------------------------
 * Each legacy ISML <isslot> is migrated to its own Page Designer page of type
 * "Content Slot Page" (see cartridges/.../experience/pages/contentSlotPage.json),
 * with the page's `pageId` equal to the slot ID. These are fetched with the same
 * `fetchPageWithComponentData` helper used for the homepage PD page itself, via
 * the standard SCAPI `/pages/{pageId}` endpoint — there is no separate slots API.
 *
 * Slot → Page Designer pageId → static fallback:
 *   home-main           → homepage page "headerbanner" region  (pageId: "homepage")
 *   home-bottom-left    → fetchPageWithComponentData "home-bottom-left"
 *   home-bottom-center  → fetchPageWithComponentData "home-bottom-center"
 *   home-bottom-col3    → fetchPageWithComponentData "home-bottom-col3"
 *   home-bottom-right   → fetchPageWithComponentData "home-bottom-right"
 *   home-middle-col1    → fetchPageWithComponentData "home-middle-col1"
 *   home-middle-col2    → fetchPageWithComponentData "home-middle-col2"
 *   home-recomm         → ProductCarouselWithSuspense (PI/Einstein — programmatic)
 *   home-recomm-bottom  → ProductCarouselWithSuspense (PI/Einstein — programmatic)
 *
 * When a slot's Content Slot Page doesn't exist yet in Business Manager,
 * `fetchPageWithComponentData` returns null (404) and <Region> renders the
 * static errorElement fallback.
 *
 * home-recomm / home-recomm-bottom use Einstein Recommendations. When Einstein is
 * unavailable or returns no items, the loader falls back to SCAPI category search.
 */

import { redirect } from 'react-router';
import type { Route } from './+types/_app._index';
import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { fetchCarouselProducts } from '@/components/product-carousel/loaders';
import { ProductCarouselWithSuspense } from '@/components/product-carousel';
import { fetchProductRecommendations } from '@/lib/product/recommendations.server';
import { EINSTEIN_RECOMMENDERS } from '@/lib/adapters/engagement/einstein-recommenders';
import { Region } from '@/components/region';
import { fetchCategories } from '@/lib/api/categories.server';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { siteContext, resolvePrefix, type SiteContext } from '@salesforce/storefront-next-runtime/site-context';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { getLogger } from '@/lib/logger.server';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';

/**
 * Page Designer metadata for the homepage.
 *
 * These region definitions tell Page Designer which editable regions exist on the
 * homepage page in Business Manager. Each entry is surfaced as a drop zone that
 * merchants can populate with components.
 *
 * Content slot pages (home-bottom-*, home-middle-*) are separate contentSlotPage
 * pages and are NOT listed here — they have their own "content" region.
 */
@PageType({
    name: 'Home Page',
    description: 'Main landing page with hero carousel, featured products, and help sections',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'topheaderbanner',
        name: 'Top Header Banner Region',
        description: 'Top area. Max 3 components.',
        maxComponents: 3,
        // This region is rendered in the shared header via <Region regionId="top-headerbanner" />.
    },
    {
        id: 'headerbanner',
        name: 'Header Banner Region',
        description: 'Hero/banner area. Max 3 components.',
        maxComponents: 3,
        // This region is rendered in the shared header via <Region regionId="headerbanner" />.
    },
    {
        id: 'main',
        name: 'Main Content Region',
        description: 'Primary page body  (home-main slot equivalent) managed by Page Designer.',
        maxComponents: 10,
        // This region is rendered in the homepage template via <Region regionId="main" />.
    },
])
export class HomePageMetadata { }

function fetchHomeRecommendationHits(
    args: Route.LoaderArgs,
    recommenderName: string,
    limit: number,
    currency?: string
): Promise<ShopperSearch.schemas['ProductSearchResult']> {
    return fetchProductRecommendations(
        { context: args.context, request: args.request },
        {
            name: recommenderName,
            ...(currency ? { currency } : {}),
        }
    ).then((einsteinResult) => {
        if (einsteinResult.recs && einsteinResult.recs.length > 0) {
            return {
                hits: einsteinResult.recs,
            } as ShopperSearch.schemas['ProductSearchResult'];
        }

        return fetchCarouselProducts(args.context, {
            categoryId: 'root',
            limit,
            currency,
        });
    });
}

export type HomePageData = {
    /** Homepage Page Designer page (headerbanner + main regions). */
    page: ReturnType<typeof fetchPageWithComponentData>;

    // ── Content Slots — one Promise per legacy <isslot> ────────────────────────
    // Each is its own Content Slot Page (pageId = slot id), fetched via
    // fetchPageWithComponentData like any other Page Designer page.
    // null = page not configured in BM → Region renders errorElement (static fallback).
    /** home-bottom-left  → <isslot id="home-bottom-left" context="global" /> */
    slotHomeBottomLeft: ReturnType<typeof fetchPageWithComponentData>;
    /** home-bottom-center → <isslot id="home-bottom-center" context="global" /> */
    slotHomeBottomCenter: ReturnType<typeof fetchPageWithComponentData>;
    /** home-bottom-col3  → <isslot id="home-bottom-col3" context="global" /> */
    slotHomeBottomCol3: ReturnType<typeof fetchPageWithComponentData>;
    /** home-bottom-right → <isslot id="home-bottom-right" context="global" /> */
    slotHomeBottomRight: ReturnType<typeof fetchPageWithComponentData>;
    /** home-middle-col1  → <isslot id="home-middle-col1" context="global" /> */
    slotHomeMiddleCol1: ReturnType<typeof fetchPageWithComponentData>;
    /** home-middle-col2  → <isslot id="home-middle-col2" context="global" /> */
    slotHomeMiddleCol2: ReturnType<typeof fetchPageWithComponentData>;

    // ── Recommendation Carousels (programmatic — Einstein + fallback) ──────────
    /** home-recomm — top Einstein recommendation carousel */
    searchResult: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    /** home-recomm-bottom — bottom Einstein recommendation carousel */
    searchResultBottom: Promise<ShopperSearch.schemas['ProductSearchResult']>;

    categories: Promise<ShopperProducts.schemas['Category'][]>;
    wishlistInitialState: Promise<WishlistInitialState>;
    pageUrl: string;
    ogImageUrl: string;
};

/**
 * Server-side loader — all fetches run in parallel (non-async streaming pattern).
 * React Router streams each Promise to the client as it resolves.
 *
 * Convention notes for this project:
 * - Keep this loader non-async so unresolved Promises can be returned directly.
 * - Create async work here (not in component render) to preserve Promise identity.
 * - Only compute/throw synchronously for URL routing/SEO-critical values.
 */
export function loader(args: Route.LoaderArgs): HomePageData {
    const logger = getLogger(args.context);
    logger.debug('HomePage: loader starting');

    const config = getConfig(args.context);
    const requestUrl = new URL(args.request.url);

    // Redirect bare "/" to the default site/locale prefixed homepage
    if (requestUrl.pathname === '/' && config.url?.prefix && config.url.prefix !== '/') {
        const siteRef = config.siteAliasMap?.[config.defaultSiteId] ?? config.defaultSiteId;
        const defaultSite = config.commerce.sites.find((s) => s.id === config.defaultSiteId);
        const defaultLocale = defaultSite?.defaultLocale ?? config.i18n.fallbackLng;
        const localeRef = config.localeAliasMap?.[defaultLocale] ?? defaultLocale;
        const prefixedPath = resolvePrefix({
            prefix: config.url.prefix,
            params: { siteId: siteRef, localeId: localeRef },
        });
        throw redirect(`${prefixedPath}/`);
    }

    const currency = (args.context.get(siteContext) as SiteContext).currency;
    // Critical SEO URL metadata is resolved eagerly on the server.
    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);

    const homepagePage = fetchPageWithComponentData(args, { pageId: 'sfnext-homepage' });

    // Return Promises without awaiting to enable deferred streaming and avoid
    // blocking route rendering on non-critical content.
    return {
        // ── Homepage PD page (headerbanner / main regions) ──────────────────────
        page: homepagePage,

        // ── Content Slots — each is its own Content Slot Page (pageId = slot id) ──
        // Fetched via fetchPageWithComponentData (/pages/{pageId}), same as the
        // homepage PD page above. Returns null when the page has no BM
        // configuration → errorElement shown.
        slotHomeBottomLeft: fetchPageWithComponentData(args, { pageId: 'sfnext-home-bottom-left' }),
        slotHomeBottomCenter: fetchPageWithComponentData(args, { pageId: 'sfnext-home-bottom-center' }),
        slotHomeBottomCol3: fetchPageWithComponentData(args, { pageId: 'sfnext-home-bottom-col3' }),
        slotHomeBottomRight: fetchPageWithComponentData(args, { pageId: 'sfnext-home-bottom-right' }),
        slotHomeMiddleCol1: fetchPageWithComponentData(args, { pageId: 'sfnext-home-middle-col1' }),
        slotHomeMiddleCol2: fetchPageWithComponentData(args, { pageId: 'sfnext-home-middle-col2' }),

        // ── Recommendation Carousels (Einstein + category-search fallback) ──────
        searchResult: fetchHomeRecommendationHits(
            args,
            EINSTEIN_RECOMMENDERS.EMPTY_SEARCH_RESULTS_TOP_SELLERS,
            config.pages.home.featuredProductsCount,
            currency ?? undefined
        ),
        searchResultBottom: fetchHomeRecommendationHits(
            args,
            EINSTEIN_RECOMMENDERS.EMPTY_SEARCH_RESULTS_MOST_VIEWED,
            config.pages.home.featuredProductsCount,
            currency ?? undefined
        ),

        categories: fetchCategories(args.context, 'root', 1),
        wishlistInitialState: fetchWishlistInitialState(args.context),
        pageUrl,
        ogImageUrl: new URL('/images/home-banner.jpg', requestUrl.origin).href,
    };
}

/**
 * HomePage — renders the homepage with all migrated content slots.
 *
 * Each <Region> follows the Salesforce content-slot migration pattern:
 *   • page    = a Promise<contentSlotPage | null> fetched per slot
 *   • regionId = "content"  (the single region inside contentSlotPage)
 *
 * Missing Page Designer content renders nothing until the merchant authors it.
 */
export default function HomePage({ loaderData }: { loaderData: HomePageData }) {
    return (
        <WishlistProvider initialState={loaderData.wishlistInitialState}>
            <div className="pb-16 -mt-8 flex flex-col lg:gap-5">
                <SeoMeta
                    rawTitle
                    title="St. Jude Gift Shop"
                    description="Shop the St. Jude Gift Shop. All purchases benefit the patients of St. Jude Children's Research Hospital."
                    openGraph={{
                        type: 'website',
                        url: loaderData.pageUrl,
                        image: loaderData.ogImageUrl,
                    }}
                />
                <Region
                    page={loaderData.page}
                    regionId="headerbanner"
                />
                {/*
                 * home-main slot
                 * Legacy: <isslot id="home-main" description="Flash Banner" context="global" />
                 * Modern: headerbanner region inside the homepage PD page.
                 * Fallback: static hero image until PD components are authored.
                 */}

                <Region
                    page={loaderData.page}
                    regionId="main"
                />

                {/*
                 * home-bottom-left / -center / -col3 / -right slots
                 * Legacy: 4 × <isslot id="home-bottom-*" context="global" /> in col-3 grid
                 * Modern: 4 independent contentSlotPage pages (pageId = slot id).
                 *   regionId="content" is the single region in contentSlotPage.json.
                 *   Fallback: ConsoleCard static image links per column.
                 * Schema.org microdata preserved from the original ISML markup.
                 */}
                <div
                    className="grid grid-cols-2 sm:grid-cols-4 w-full"
                    itemProp="mainEntity"
                    itemScope
                    itemType="http://schema.org/WebPageElement">
                    <Region page={loaderData.slotHomeBottomLeft} regionId="content" />
                    <Region page={loaderData.slotHomeBottomCenter} regionId="content" />
                    <Region page={loaderData.slotHomeBottomCol3} regionId="content" />
                    <Region page={loaderData.slotHomeBottomRight} regionId="content" />
                </div>

                {/*
                 * home-middle-col1 / home-middle-col2 slots
                 * Legacy: <isslot id="home-middle-col1" /> (col-4) + <isslot id="home-middle-col2" /> (col-8)
                 * Modern: 2 independent contentSlotPage pages.
                 *   regionId="content" — the single region in contentSlotPage.json.
                 *   Fallback: OliviaBannerLeft (image) + OliviaBannerRight (copy).
                 * Schema.org microdata preserved from the original ISML markup.
                 */}
                <div
                    className="flex w-full"
                    itemProp="mainEntity"
                    itemScope
                    itemType="http://schema.org/WebPageElement">
                    <div className="w-1/2">
                        <Region page={loaderData.slotHomeMiddleCol1} regionId="content" />
                    </div>
                    <div className="w-1/2">
                        <Region page={loaderData.slotHomeMiddleCol2} regionId="content" />
                    </div>
                </div>

                {/*
                 * home-recomm-bottom slot
                 * Legacy: <isslot id="home-recomm-bottom" description="HOMEPAGE - PI Recommendation" context="global" />
                 * Uses Einstein with category-search fallback when no recs are returned.
                 */}
                <div className="w-full py-6 lg:py-8">
                    <ProductCarouselWithSuspense
                        resolve={loaderData.searchResultBottom}
                        title="Trending St. Jude Apparel"
                    />
                </div>

                {/**
                 * home-recomm slot
                 * Legacy: <isslot id="home-recomm" description="HOMEPAGE - PI Recommendation" context="global" />
                 * Uses Einstein with category-search fallback when no recs are returned.
                 */}
                <div className="w-full py-6 lg:py-8">
                    <ProductCarouselWithSuspense
                        resolve={loaderData.searchResult}
                        title="Recommended for You"
                    />
                </div>


            </div>
        </WishlistProvider>
    );
}
